import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGameHistory, deleteGameResult } from '../Shared/utils/HistoryUtils';
import { ALL_DRILLS, BELT_LEVELS, BELT_COLORS } from './DrillConstants';
import GameLayout from '../Shared/components/GameLayout/GameLayout';
import HistoryModal from '../Shared/components/HistoryModal/HistoryModal';
import * as styles from './SamuraiStyles';

const SamuraiStats = () => {
    const navigate = useNavigate();
    const [selectedDrillName, setSelectedDrillName] = useState(null);
    const [history, setHistory] = useState(() => getGameHistory('modern-samurai'));

    const stats = useMemo(() => {
        if (!history || history.length === 0) return null;

        const drillStats = {};
        
        // Initialize drill stats
        ALL_DRILLS.forEach(drill => {
            drillStats[drill.id] = {
                id: drill.id,
                name: drill.name,
                totalRuns: 0,
                belts: { miss: 0, yellow: 0, green: 0, blue: 0, purple: 0, brown: 0, black: 0 },
                totalBeltScore: 0,
                runs: [] 
            };
        });

        // Belt numeric values for "ability to improve" calculation
        const BELT_VALUES = { miss: 0, yellow: 1, green: 2, blue: 3, purple: 4, brown: 5, black: 6 };

        history.forEach(run => {
            const drillId = run.drill;
            if (drillStats[drillId]) {
                drillStats[drillId].totalRuns++;
                const belt = run.belt || 'miss';
                drillStats[drillId].belts[belt]++;
                drillStats[drillId].totalBeltScore += BELT_VALUES[belt];
                drillStats[drillId].runs.push(run);
            }
        });

        const drillsWithRuns = Object.values(drillStats).filter(d => d.totalRuns > 0);
        
        if (drillsWithRuns.length === 0) return null;

        let mostAbilityToImprove = drillsWithRuns[0];
        let lowestAvgScore = mostAbilityToImprove.totalBeltScore / mostAbilityToImprove.totalRuns;

        drillsWithRuns.forEach(d => {
            const avgScore = d.totalBeltScore / d.totalRuns;
            if (avgScore < lowestAvgScore) {
                lowestAvgScore = avgScore;
                mostAbilityToImprove = d;
            }
        });

        return {
            drills: drillsWithRuns,
            mostAbilityToImprove
        };
    }, [history]);

    const handleDeleteDataPoint = (id) => {
        if (window.confirm('Delete this result permanently?')) {
            deleteGameResult('modern-samurai', id);
            setHistory(getGameHistory('modern-samurai'));
        }
    };

    const renderBeltBar = (drill) => {
        const total = drill.totalRuns;
        if (total === 0) return null;

        const belts = ['black', 'brown', 'purple', 'blue', 'green', 'yellow', 'miss'];
        
        return (
            <div style={{ display: 'flex', width: '100%', height: '24px', borderRadius: '4px', overflow: 'hidden', marginTop: '8px' }}>
                {belts.map(belt => {
                    const count = drill.belts[belt];
                    if (count === 0) return null;
                    const percent = (count / total) * 100;
                    const color = belt === 'miss' ? '#e74c3c' : BELT_COLORS[belt];
                    return (
                        <div 
                            key={belt} 
                            style={{ 
                                width: `${percent}%`, 
                                backgroundColor: color, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                color: belt === 'yellow' || belt === 'white' ? '#000' : '#fff',
                                textShadow: belt === 'yellow' ? 'none' : '0px 0px 2px rgba(0,0,0,0.8)'
                            }}
                            title={`${belt}: ${count} (${percent.toFixed(1)}%)`}
                        >
                            {percent > 8 ? `${percent.toFixed(0)}%` : ''}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <GameLayout
            gameRunning={false}
            infoButton={
                <button
                    onClick={() => navigate('/modern-samurai')}
                    style={{
                        padding: '8px 16px',
                        background: '#1a3a5c',
                        color: '#5dade2',
                        border: '1px solid #2c5282',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                    }}
                >
                    Back to Trainer
                </button>
            }
        >
            <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', width: '100%', color: '#e0e6f0' }}>
                <h1 style={{ color: '#5dade2', textAlign: 'center', marginBottom: '30px' }}>Drill Performance</h1>

                {!stats ? (
                    <div style={{ textAlign: 'center', padding: '40px', background: '#1a1a2e', borderRadius: '8px' }}>
                        <h2 style={{ color: '#888' }}>No Data Available</h2>
                        <p style={{ color: '#666' }}>Play some drills to see your statistics here.</p>
                    </div>
                ) : (
                    <>
                        <div style={{ 
                            background: '#1a1a2e', 
                            border: '1px solid #e74c3c44', 
                            borderRadius: '8px', 
                            padding: '20px', 
                            marginBottom: '30px',
                            textAlign: 'center'
                        }}>
                            <h2 style={{ color: '#e74c3c', fontSize: '16px', marginTop: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                Drill With Most Ability To Improve
                            </h2>
                            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#fff', marginBottom: '10px' }}>
                                {stats.mostAbilityToImprove.name}
                            </div>
                            <p style={{ color: '#aaa', fontSize: '14px', margin: 0 }}>
                                Focus on this drill to quickly level up your overall performance.
                            </p>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h2 style={{ color: '#fff', fontSize: '20px', margin: 0 }}>Belt Distribution by Drill</h2>
                                <span style={{ fontSize: '12px', color: '#666' }}>Click a drill to see detailed history</span>
                            </div>
                            
                            {stats.drills.map(drill => (
                                <div 
                                    key={drill.id} 
                                    style={{ 
                                        background: '#1a1a2e', 
                                        borderRadius: '8px', 
                                        padding: '15px', 
                                        border: '1px solid #333',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                    }}
                                    onClick={() => setSelectedDrillName(drill.name)}
                                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#5dade2'}
                                    onMouseLeave={(e) => e.currentTarget.style.borderColor = '#333'}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{drill.name}</div>
                                        <div style={{ fontSize: '12px', color: '#888' }}>{drill.totalRuns} total runs</div>
                                    </div>
                                    
                                    {renderBeltBar(drill)}
                                    
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px', fontSize: '11px', flexWrap: 'wrap' }}>
                                        {['black', 'brown', 'purple', 'blue', 'green', 'yellow', 'miss'].map(belt => {
                                            if (drill.belts[belt] === 0) return null;
                                            return (
                                                <div key={belt} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: belt === 'miss' ? '#e74c3c' : BELT_COLORS[belt] }} />
                                                    <span style={{ color: '#aaa', textTransform: 'capitalize' }}>{belt}: {drill.belts[belt]}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <HistoryModal
                    isOpen={!!selectedDrillName}
                    onClose={() => setSelectedDrillName(null)}
                    history={history}
                    gameName="Modern Samurai"
                    filterKey="drillName"
                    filterValue={selectedDrillName}
                    onDeletePoint={handleDeleteDataPoint}
                    parTimes={ALL_DRILLS.find(d => d.name === selectedDrillName)?.parTimes}
                />
            </div>
        </GameLayout>
    );
};

export default SamuraiStats;
